import React, { useEffect, useState, useCallback } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import WebView from "react-native-webview";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { useRoute, useFocusEffect, RouteProp } from "@react-navigation/native";
import CustomHtml from "../components/common/general/CustomHtml/CustomHtml";
import RoundedLabel from "../components/common/label/RoundedLabel/RoundedLabel";
import { isSubmissionLate } from "../utils/date";
import { File, FileStatus, StoragePathType, useSubmitStudentAssignmentMutation } from "../graphql/generated/graphql";
import Skeleton from "react-loading-skeleton";
import { useAppState } from "../contexts/AppStateContext";
import type { RootStackParamList } from "../navigation/navigation.types";
import NewCustomAxioActionModal from "../containers/settings/UsersTabContainer/NewCustomAxioActionModal";
import { shadowStyles } from "../styles/shadow";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { ResourceType, useActiveCourse } from "../contexts/ActiveCourseContext";
import { getEmbeddedVideoUrl } from "../utils/regex.dictionary";
import StudentFileUpload from "../components/customCoursework/StudentFileUpload/StudentFileUpload";
import ResourceCard from "../components/customCoursework/StudentFileUpload/ResourceCard";

// Grade display component
const GradeDisplay = ({ grade, feedback, feedbackFiles }: { grade: number | null; feedback: string | null; feedbackFiles: Partial<File>[] | null }) => {
  if (grade === null || grade === undefined) {
    return null;
  }

  return (
    <View style={styles.sectionContainer}>
      <CustomText
        text="Assignment Grade"
        size="m"
        weight="ultraBold"
        useTranslationText={false}
        aria-level="2"
        role="heading"
      />
      <CustomSpacing type="vertical" size="m" />
      <View style={styles.gradeContainer}>
        <RoundedLabel
          text={`${grade}/100`}
          style={styles.gradedLabel}
          textStyle={styles.gradedLabelText}
          padding={[12, 8]}
          isRounded={true}
        />
      </View>

      {feedback && (
        <>
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            text="Teacher Feedback:"
            size="s"
            weight="bold"
            useTranslationText={false}
            aria-level="3"
            role="heading"
          />
          <CustomSpacing type="vertical" size="xs" />
          <View style={styles.feedbackContainer}>
            <CustomText text={feedback} size="s" useTranslationText={false} style={styles.feedbackText} />
          </View>
        </>
      )}

      {feedbackFiles && feedbackFiles.length > 0 && (
        <>
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            text="Feedback Attachments:"
            size="s"
            weight="bold"
            useTranslationText={false}
            aria-level="3"
            role="heading"
          />
          <CustomSpacing type="vertical" size="xs" />
          <View style={styles.filesContainer}>
            {feedbackFiles.map((file) => (
              <ResourceCard key={file.id} file={file} showDate={false} />
            ))}
          </View>
        </>
      )}
    </View>
  );
};

function CustomAssignmentStudentView() {
  const route = useRoute<RouteProp<RootStackParamList, "Custom Assignment Student">>();
  const { resource_type: resourceType, resource_id: resourceId } = route.params;

  const [submitStudentAssignment] = useSubmitStudentAssignmentMutation();
  const [submissionConfirmationModalVisible, setSubmissionConfirmationModalVisible] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showPastSubmissions, setShowPastSubmissions] = useState(false);

  const { dispatch } = useAppState();

  const {
    assignmentData,
    loadingAssignment,
    setActiveResource,
    activeResource,
    refetchUserSubmissions,
    userSubmissionsData,
  } = useActiveCourse();

  useEffect(() => {
    setActiveResource({ resourceType: resourceType as ResourceType, resourceId });
  }, [resourceType, resourceId]);

  const [files, setFiles] = useState<Partial<File>[]>([]);

  // Helper function to get the submission that's waiting for grading
  const getWaitingSubmission = () => {
    if (!userSubmissionsData?.getUserSubmissions?.data) {
      return null;
    }
    return userSubmissionsData.getUserSubmissions.data.find(
      (submission) => submission.isCompleted && (submission.grade === null || submission.grade === undefined)
    );
  };

  const isWaitingForGrading = () => {
    return getWaitingSubmission() !== undefined && getWaitingSubmission() !== null;
  };

  const getGradedSubmission = () => {
    if (!userSubmissionsData?.getUserSubmissions?.data) {
      return null;
    }
    return userSubmissionsData.getUserSubmissions.data.find(
      (submission) => submission.grade !== null && submission.grade !== undefined
    );
  };

  useEffect(() => {
    setFiles([]);
    if (activeResource?.resourceId) {
      refetchUserSubmissions();
    }
  }, [refetchUserSubmissions, activeResource?.resourceId]);

  useEffect(() => {
    const waitingSubmission = getWaitingSubmission();
    if (waitingSubmission?.files) {
      setFiles(waitingSubmission.files || []);
    } else {
      const gradedSubmission = getGradedSubmission();
      if (gradedSubmission?.files) {
        setFiles(gradedSubmission.files || []);
      } else if (userSubmissionsData?.getUserSubmissions?.data?.[0]?.files) {
        setFiles(userSubmissionsData.getUserSubmissions.data[0].files || []);
      } else {
        setFiles([]);
      }
    }
  }, [userSubmissionsData]);

  // Helper functions for grade display
  const hasGrade = () => {
    if (isWaitingForGrading()) {
      return false;
    }

    // First check if we have a submission
    if (!userSubmissionsData?.getUserSubmissions?.data?.[0]) {
      return false;
    }

    // Then check if it has been graded
    const submission = userSubmissionsData.getUserSubmissions.data[0];
    return submission.grade !== null && submission.grade !== undefined;
  };

  const getSubmissionGrade = () => {
    if (!hasGrade()) {
      return null;
    }
    const gradedSubmission = getGradedSubmission();
    return gradedSubmission?.grade || null;
  };

  const getSubmissionFeedback = () => {
    if (!hasGrade()) {
      return null;
    }
    const gradedSubmission = getGradedSubmission();
    return gradedSubmission?.feedback || null;
  };

  const getSubmissionFeedbackFiles = () => {
    if (!hasGrade()) {
      return null;
    }
    const gradedSubmission = getGradedSubmission();
    return gradedSubmission?.feedbackFiles ?? null;
  };

  const getCurrentUserRetakeConnection = () => {
    const assignment = assignmentData?.getAssignment?.data?.[0];

    if (!assignment?.usersHasSchoolAssignmentConnection?.edges?.length) {
      return null;
    }

    // Since the backend filters by current user, there should only be one edge (or none)
    return assignment.usersHasSchoolAssignmentConnection.edges[0] as any;
  };

  const getRetakeDeadline = () => {
    const connection = getCurrentUserRetakeConnection();
    return connection?.properties?.retakeDeadline;
  };

  const isRetakeAllowed = () => {
    const connection = getCurrentUserRetakeConnection();
    const retakeAllowed = connection?.properties?.retakeAllowed;

    return retakeAllowed === true;
  };

  const formatDeadlineDate = (deadline: string) => {
    const date = new Date(deadline);
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isDeadlinePassed = () => {
    const now = new Date().toISOString();
    const assignmentDueDate = assignmentData?.getAssignment?.data?.[0]?.due_date;
    const retakeDeadline = hasSubmissions() ? getRetakeDeadline() : null;

    return isSubmissionLate({
      submittedDate: now,
      dueDate: assignmentDueDate,
      retakeDeadline: retakeDeadline,
    });
  };

  const hasSubmissions = () => {
    return userSubmissionsData?.getUserSubmissions?.data?.length > 0;
  };

  // Get all graded submissions (excluding the latest one which is shown in main view)
  const getPastSubmissions = () => {
    if (!userSubmissionsData?.getUserSubmissions?.data) {
      return [];
    }

    const gradedSubmissions = userSubmissionsData.getUserSubmissions.data
      .filter((submission) => submission.grade !== null && submission.grade !== undefined)
      .sort((a, b) => {
        const dateA = a.completedAt ? new Date(a.completedAt).getTime() : a.created ? new Date(a.created).getTime() : 0;
        const dateB = b.completedAt ? new Date(b.completedAt).getTime() : b.created ? new Date(b.created).getTime() : 0;
        return dateB - dateA;
      });

    if (gradedSubmissions.length <= 1) {
      return [];
    }

    return gradedSubmissions.slice(1);
  };

  const hasPastSubmissions = () => {
    return getPastSubmissions().length > 0;
  };

  useFocusEffect(
    useCallback(() => {
      dispatch({
        type: "SET_MEDIA",
        payload: {
          video: null,
          wiki: null,
        },
      });

      return () => {
        dispatch({
          type: "SET_MEDIA",
          payload: {
            video: null,
            wiki: null,
          },
        });
      };
    }, [dispatch])
  );

  const handleSubmitAssignment = async () => {
    const pendingFiles = files.filter((file) => file.status === FileStatus.Pending);
    if (pendingFiles.length === 0) {
      return;
    }

    if (hasSubmissions() && hasGrade()) {
      const connection = getCurrentUserRetakeConnection();
      const retakeAllowed = connection?.properties?.retakeAllowed;

      if (!connection || !retakeAllowed) {
        setErrorMessage("Resubmission is not allowed for this assignment.");
        setErrorModalVisible(true);
        return;
      }
    }

    try {
      await submitStudentAssignment({
        variables: {
          assignmentId: activeResource?.resourceId,
          files: pendingFiles.map((file) => ({ fileId: file.id, fileName: file.fileName, fileURL: file.fileURL })),
        },
      });
      setSubmissionConfirmationModalVisible(true);
    } catch (error) {
      setErrorMessage(error.message || "Failed to submit assignment. Please try again.");
      setErrorModalVisible(true);
    }
  };

  const handleCloseSubmissionModal = () => {
    setSubmissionConfirmationModalVisible(false);
    refetchUserSubmissions();
  };

  const renderSubmittedAssignments = () => {
    // Filter to only show submitted files (not pending)
    const submittedFiles = files.filter((file) => file.status !== FileStatus.Pending);

    if (!submittedFiles || submittedFiles.length === 0) {
      return (
        <View style={styles.noSubmissionsContainer}>
          <CustomText
            text="Looks like you haven't submitted anything for this assignment."
            size="m"
            useTranslationText={false}
            style={{ justifyContent: "center", textAlign: "center" }}
          />
        </View>
      );
    }

    return (
      <View style={styles.filesContainer}>
        {submittedFiles.map((file) => (
          <ResourceCard key={file.id} file={file} showDate={true} />
        ))}
      </View>
    );
  };

  const renderSkeletonLoader = () => (
    <View style={styles.skeletonContainer}>
      <View style={styles.detailsContainer}>
        <View style={styles.headerContainer}>
          <Skeleton width={40} height={40} circle={true} />
          <CustomSpacing type="horizontal" size="m" />
          <Skeleton width="60%" height={24} />
        </View>
        <CustomSpacing type="vertical" size="m" />
        <CustomSpacing type="vertical" size="l" />
        <Skeleton width="30%" height={40} borderRadius={8} />
        <CustomSpacing type="vertical" size="l" />
        <Skeleton width="100%" height={200} borderRadius={12} />
      </View>
    </View>
  );

  const AssignmentVideoPlayer = ({ videoId }) => {
    const embeddedUrl = getEmbeddedVideoUrl(videoId);
    const urlWithCaptions = embeddedUrl ? `${embeddedUrl}?cc_load_policy=1` : null;
    return (
      <View style={styles.fullWidthVideoContainer} aria-labelledby="video-title">
        <WebView
          style={styles.webView}
          source={{
            uri: urlWithCaptions,
          }}
          javaScriptEnabled={true}
          allowsFullscreenVideo={true}
          domStorageEnabled={true}
        />
      </View>
    );
  };

  const renderPastSubmissions = () => {
    const pastSubmissions = getPastSubmissions();

    if (!hasPastSubmissions()) {
      return null;
    }

    return (
      <View style={styles.pastSubmissionsContainer}>
        <Pressable
          onPress={() => setShowPastSubmissions(!showPastSubmissions)}
          style={styles.pastSubmissionsHeader}
          accessibilityRole="button"
          accessibilityLabel={showPastSubmissions ? "Hide past submissions" : "Show past submissions"}>
          <CustomText
            text={showPastSubmissions ? "Hide Past Submissions" : "Past Submissions"}
            size="s"
            weight="bold"
            style={styles.pastSubmissionsButtonText}
            useTranslationText={false}
          />
        </Pressable>

        {showPastSubmissions && (
          <View style={styles.pastSubmissionsList}>
            {pastSubmissions.map((submission, index) => (
              <View key={submission.id} style={styles.pastSubmissionItem}>
                <View style={styles.pastSubmissionHeader}>
                  <CustomText
                    text={`Submission ${pastSubmissions.length - index}`}
                    size="xs"
                    weight="bold"
                    aria-level="4"
                    role="heading"
                    style={styles.pastSubmissionTitle}
                    useTranslationText={false}
                  />
                  {(submission.completedAt || submission.created) && (
                    <CustomText
                      text={new Date(submission.completedAt || submission.created).toLocaleDateString()}
                      size="xs"
                      style={styles.pastSubmissionDate}
                      useTranslationText={false}
                    />
                  )}
                </View>

                {submission.grade !== null && submission.grade !== undefined && (
                  <View style={styles.pastSubmissionGrade}>
                    <RoundedLabel
                      text={`${submission.grade}/100`}
                      style={styles.pastSubmissionGradeLabel}
                      textStyle={styles.pastSubmissionGradeText}
                      padding={[6, 8]}
                      isRounded={true}
                    />
                  </View>
                )}

                {submission.feedback && (
                  <View style={styles.pastSubmissionFeedback}>
                    <CustomText
                      text={submission.feedback}
                      size="xs"
                      style={styles.pastSubmissionFeedbackText}
                      useTranslationText={false}
                    />
                  </View>
                )}

                {submission.files && submission.files.length > 0 && (
                  <View style={styles.pastSubmissionFiles}>
                    {submission.files.map((file) => (
                      <ResourceCard key={file.id} file={file} showDate={false} />
                    ))}
                  </View>
                )}

                {index < pastSubmissions.length - 1 && <View style={styles.pastSubmissionDivider} />}
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderMainContent = () => {
    if (loadingAssignment) {
      return renderSkeletonLoader();
    }

    return (
      <>
        <View style={styles.contentRow}>
          <View style={styles.leftColumn}>
            <CustomSpacing type="vertical" size="l" />

            {assignmentData?.getAssignment?.data?.[0]?.description && (
              <View style={styles.descriptionContainer}>
                <CustomText text="Instructions" size="m" weight="ultraBold" aria-level="2" role="heading" />
                <CustomSpacing type="vertical" size="s" />
                <CustomHtml html={assignmentData.getAssignment.data[0].description} />
              </View>
            )}

            {assignmentData?.getAssignment?.data?.[0]?.videoUrl && (
              <View
                style={styles.sectionContainer}
                aria-label="Video"
                role="region"
                accessibilityLabel="Assignment Video">
                <CustomText text="Video" size="m" weight="ultraBold" aria-level="2" role="heading" />
                <CustomSpacing type="vertical" size="s" />
                {assignmentData?.getAssignment?.data?.[0]?.videoUrl && (
                  <AssignmentVideoPlayer videoId={assignmentData.getAssignment.data[0].videoUrl} />
                )}
              </View>
            )}

            {assignmentData?.getAssignment?.data?.[0]?.worksheet && (
              <View style={styles.sectionContainer}>
                <CustomText text="Worksheet" size="m" weight="ultraBold" aria-level="2" role="heading" />
                <CustomSpacing type="vertical" size="s" />
                {assignmentData?.getAssignment?.data?.[0]?.worksheet?.files?.length > 0 && (
                  <>
                    <View style={styles.filesContainer}>
                      {assignmentData?.getAssignment?.data?.[0]?.worksheet?.files.map((file) => (
                        <ResourceCard key={file.id} file={file} showDate={false} />
                      ))}
                    </View>
                    <CustomSpacing type="vertical" size="m" />
                  </>
                )}
              </View>
            )}
          </View>

          <View style={styles.rightColumn}>
            {hasGrade() && (
              <GradeDisplay
                grade={getSubmissionGrade()}
                feedback={getSubmissionFeedback()}
                feedbackFiles={getSubmissionFeedbackFiles()}
              />
            )}

            {assignmentData?.getAssignment?.data?.[0]?.files?.length > 0 && (
              <View style={styles.sectionContainer}>
                <CustomText text="Assignment Resources" size="m" weight="ultraBold" aria-level="2" role="heading" />
                <CustomSpacing type="vertical" size="s" />
                <View style={styles.filesContainer}>
                  {assignmentData.getAssignment.data[0].files.map((file) => (
                    <ResourceCard key={file.id} file={file} showDate={false} />
                  ))}
                </View>
              </View>
            )}

            {isWaitingForGrading() && (
              <View style={styles.sectionContainer}>
                <CustomText text="Upload Assignment" size="m" weight="ultraBold" aria-level="2" role="heading" />
                <CustomSpacing type="vertical" size="m" />
                <View style={styles.submittedContainer}>
                  <CustomText
                    text="Submitted"
                    size="m"
                    weight="bold"
                    style={styles.submittedText}
                    useTranslationText={false}
                  />
                  <CustomSpacing type="vertical" size="xs" />
                  <CustomText
                    useTranslationText={false}
                    text="Your assignment has been submitted and is waiting to be graded by your teacher."
                    size="s"
                    style={styles.submissionInfoText}
                  />
                </View>
              </View>
            )}

            <View style={styles.sectionContainer}>
              <CustomText text="What you submitted" size="m" weight="ultraBold" aria-level="2" role="heading" />
              <CustomSpacing type="vertical" size="m" />
              {renderSubmittedAssignments()}
            </View>

            {assignmentData?.getAssignment?.data?.[0]?.enableStudentFileUpload &&
              !isWaitingForGrading() &&
              (!hasSubmissions() || (hasGrade() && isRetakeAllowed())) && (
                <View style={styles.sectionContainer}>
                  <CustomText text="Upload Assignment" size="m" weight="ultraBold" aria-level="2" role="heading" />
                  <CustomSpacing type="vertical" size="m" />

                  {isRetakeAllowed() && hasGrade() && (
                    <>
                      <CustomText
                        text="You can resubmit this assignment. Your new submission will be reviewed by your teacher."
                        size="s"
                        style={styles.retakeInfoText}
                      />
                      {getRetakeDeadline() && (
                        <>
                          <CustomSpacing type="vertical" size="xs" />
                          <CustomText
                            text={`Resubmission deadline: ${formatDeadlineDate(getRetakeDeadline())}`}
                            size="s"
                            style={styles.deadlineText}
                            useTranslationText={false}
                          />
                        </>
                      )}
                      <CustomSpacing type="vertical" size="m" />
                    </>
                  )}

                  {!hasSubmissions() ||
                  !userSubmissionsData?.getUserSubmissions?.data?.[0]?.isCompleted ||
                  isRetakeAllowed() ? (
                    <>
                      {isDeadlinePassed() ? (
                        <CustomText
                          text={
                            hasSubmissions()
                              ? "Submissions are closed. The resubmission deadline was {{deadline}}."
                              : "Submissions are closed. The assignment deadline was {{deadline}}."
                          }
                          interpolation={{
                            deadline: hasSubmissions()
                              ? getRetakeDeadline()
                                ? formatDeadlineDate(getRetakeDeadline())
                                : ""
                              : assignmentData?.getAssignment?.data?.[0]?.due_date
                                ? formatDeadlineDate(assignmentData.getAssignment.data[0].due_date)
                                : "",
                          }}
                          size="s"
                          style={styles.deadlineWarningText}
                        />
                      ) : (
                        <StudentFileUpload
                          resource={activeResource}
                          filesState={[files, setFiles]}
                          storagePath={StoragePathType.Resources}
                          onSubmitFiles={handleSubmitAssignment}
                        />
                      )}
                    </>
                  ) : (
                    <CustomText
                      text="You have already submitted this assignment. Your submission is being reviewed."
                      size="s"
                      style={styles.submissionInfoText}
                    />
                  )}
                </View>
              )}

            {renderPastSubmissions()}
          </View>
        </View>
      </>
    );
  };

  return (
    <DefaultLayout showCourseNav={true} pageHasChat={false}>
      {renderMainContent()}

      <NewCustomAxioActionModal
        visible={submissionConfirmationModalVisible}
        onClose={handleCloseSubmissionModal}
        modalTexts={{
          title: "Submission Confirmation",
          warningText: "You have successfully submitted your assignment.",
          ariaLabel: "Submission Confirmation",
          actionButtonText: "Close",
        }}
      />
      <NewCustomAxioActionModal
        visible={errorModalVisible}
        onClose={() => setErrorModalVisible(false)}
        modalTexts={{
          title: "Error",
          warningText: errorMessage,
          ariaLabel: "Error",
          actionButtonText: "Close",
        }}
      />
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  gradedLabel: {
    backgroundColor: "#ECFDF3",
    borderRadius: 360,
  },
  gradedLabelText: {
    color: "#027A48",
    fontSize: 12,
    fontWeight: "700",
  },
  contentRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  leftColumn: {
    width: "74%",
    paddingRight: 40,
    minWidth: 300,
  },
  rightColumn: {
    width: "25%",
    minWidth: 250,
  },
  descriptionContainer: {
    marginBottom: 20,
  },
  sectionContainer: {
    marginBottom: 30,
  },
  submissionInfoText: {
    color: "#6B7280",
  },
  submittedContainer: {
    padding: 16,
    backgroundColor: "#F0F9FF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#BAE6FD",
  },
  submittedText: {
    color: "#0369A1",
  },
  retakeInfoText: {
    color: "#3B82F6",
    fontStyle: "italic",
  },
  deadlineText: {
    color: "#059669",
    fontWeight: "600",
  },
  deadlineWarningText: {
    color: "#DC2626",
    fontStyle: "italic",
  },
  filesContainer: {
    marginBottom: 10,
    flexDirection: "column",
    gap: 10,
    width: "100%",
  },
  noSubmissionsContainer: {
    padding: 16,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginBottom: 20,
  },
  skeletonContainer: {
    flex: 1,
    padding: 20,
  },
  detailsContainer: {
    flex: 1,
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  fullWidthVideoContainer: {
    width: "100%",
    aspectRatio: 16 / 9,
    marginBottom: 20,
    overflow: "hidden",
    borderRadius: 8,
    ...shadowStyles.smallShadow,
  },
  webView: {
    width: "100%",
    height: "100%",
  },
  gradeContainer: {
    marginVertical: 8,
    alignItems: "flex-start",
  },
  feedbackContainer: {
    padding: 12,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  feedbackText: {
    color: "#374151",
    lineHeight: 20,
  },
  pastSubmissionsContainer: {
    marginTop: 0,
    width: "100%",
  },
  pastSubmissionsHeader: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  pastSubmissionsButtonText: {
    color: "#3B82F6",
    textDecorationLine: "underline",
  },
  pastSubmissionsList: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  pastSubmissionItem: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  pastSubmissionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  pastSubmissionTitle: {
    color: "#374151",
  },
  pastSubmissionDate: {
    color: "#6B7280",
  },
  pastSubmissionGrade: {
    marginBottom: 6,
  },
  pastSubmissionGradeLabel: {
    backgroundColor: "#ECFDF3",
    borderRadius: 360,
  },
  pastSubmissionGradeText: {
    color: "#027A48",
    fontSize: 11,
    fontWeight: "600",
  },
  pastSubmissionFeedback: {
    padding: 8,
    backgroundColor: "#F9FAFB",
    borderRadius: 6,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  pastSubmissionFeedbackText: {
    color: "#374151",
    fontSize: 12,
    lineHeight: 18,
  },
  pastSubmissionFiles: {
    marginTop: 4,
  },
  pastSubmissionDivider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginVertical: 12,
  },
});

export default CustomAssignmentStudentView;
